import React, { useRef, useState } from 'react';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { useIntl, type IntlShape } from 'react-intl-next';
import { cssMap } from '@compiled/react';

import { Pressable } from '@atlaskit/primitives/compiled';
import type { CollabEditPlugin } from '@atlaskit/editor-plugin-collab-edit';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import { useSharedPluginStateSelector } from '@atlaskit/editor-common/use-shared-plugin-state-selector';
import { JSONTransformer } from '@atlaskit/editor-json-transformer';
import type { DocNode } from '@atlaskit/adf-schema';
import { token } from '@atlaskit/tokens';

import { withFlags } from '@confluence/flags';
import type { WithFlagsProps, FlagDescriptor } from '@confluence/flags';
import { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';

import { messages } from './messages';
import { createPage } from './createPageQuery';

const styles = cssMap({
	saveCopyLink: {
		backgroundColor: 'transparent',
		padding: '0',
		font: token('font.body.small'),
		fontWeight: token('font.weight.bold'),
		color: token('color.text.subtlest'),
		textDecoration: 'underline',
		'&:hover': {
			backgroundColor: token('color.background.accent.orange.subtlest.hovered'),
		},
	},
});

type CreateOfflineCopyProps = {
	chunks: React.ReactNode;
	api: PublicPluginAPI<[CollabEditPlugin]> | undefined;
	contentId: string;
	spaceId: string | undefined;
	setIsLoading: (loading: boolean) => void;
	isLivePage?: boolean;
} & WithFlagsProps;

export const CreateOfflineCopy = withFlags(
	({
		flags,
		chunks,
		api,
		contentId,
		spaceId,
		setIsLoading,
		isLivePage = false,
	}: CreateOfflineCopyProps) => {
		const isCreatingPage = useRef(false);
		const offlineDoc = useSharedPluginStateSelector(
			api,
			'collabEdit.lastReconnectionConflictMetadata',
		)?.offlineDoc;
		const [shouldUpdatePageTree, setShouldUpdatePageTree] = useState(false);
		const intl = useIntl();

		return (
			<>
				<Pressable
					testId="offline-conflict-create-page-copy"
					onClick={async () => {
						if (isCreatingPage.current || !offlineDoc || !spaceId) {
							return;
						}
						isCreatingPage.current = true;
						setIsLoading(true);
						try {
							const response = await createPage(
								spaceId,
								contentId,
								new JSONTransformer().encode(offlineDoc) as DocNode,
								isLivePage,
							);

							if (!response.ok) {
								void flags.showFlag(createErrorFlag(intl));
								return;
							}
							setShouldUpdatePageTree(true);
							void flags.hideFlag('editor_reconnection_conflict');
							const data = await response.json();
							void flags.showFlag(
								createCopySuccessFlag(`${data._links.base}${data._links.edituiv2}`, intl),
							);
						} catch (e) {
							void flags.showFlag(createErrorFlag(intl));
						} finally {
							isCreatingPage.current = false;
							setIsLoading(false);
						}
					}}
					xcss={styles.saveCopyLink}
				>
					{chunks}
				</Pressable>
				<Subscribe to={[PageTreeStateUpdater]}>
					{(pageStateUpdater: PageTreeStateUpdater) => {
						// Make sure we only push the update once
						if (shouldUpdatePageTree && contentId) {
							// Requests that we fetch the page tree with the newly restored page.
							pageStateUpdater.setForceFetchPageId(contentId);
							setShouldUpdatePageTree(false);
						}
						return null;
					}}
				</Subscribe>
			</>
		);
	},
);

const createErrorFlag = (intl: IntlShape): FlagDescriptor => ({
	id: 'editor-reconnection-conflict-copy-failed',
	type: 'error',
	title: intl.formatMessage(messages.errorFlagTitle),
	description: intl.formatMessage(messages.errorFlagDescription),
	close: 'auto',
});

const createCopySuccessFlag = (href: string, intl: IntlShape): FlagDescriptor => ({
	id: 'editor-reconnection-conflict-copy-created',
	type: 'success',
	title: intl.formatMessage(messages.copyCreatedFlagTitle),
	description: intl.formatMessage(messages.copyCreatedFlagDescription),
	close: 'auto',
	actions: [
		{
			content: intl.formatMessage(messages.copyCreatedFlagViewPage),
			href,
		},
	],
});
